import { useEffect, useState, type ReactNode } from 'react';

import { AuthContext, type AuthContextValue } from './auth-context';
import { isDevAuthEnabled } from '../dev-auth/dev-auth'; // DEV-1
import { devLogin } from '../dev-auth/dev-login'; // DEV-1
import { canUseAdminArea, hasCapability } from '../lib/access';
import { accessTokenStore, authService, isSignInError } from '../services/auth.service';
import { setSessionExpiredHandler } from '../services/http-client';
import type { AuthState, SessionUser } from '../types';

export interface AuthProviderProps {
  readonly children: ReactNode;
}

const LOADING: AuthState<SessionUser> = {
  status: 'loading',
  user: null,
  endReason: null,
  signInFailure: null,
  returnTo: null,
};

const SIGNED_OUT: AuthState<SessionUser> = { ...LOADING, status: 'unauthenticated' };

/**
 * Owns authentication state for the portal — the one piece of global state it has.
 *
 * Status, user, end reason and sign-in failure change together, so they live in one
 * state object: separate `useState` calls could render a frame that is
 * `authenticated` with no user. Server data (projects, logs) is not held here.
 *
 * No `useMemo`/`useCallback`: the provider re-renders only when the session itself
 * changes, and then every consumer needs the new value anyway.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSession] = useState<AuthState<SessionUser>>(LOADING);
  // Bumped by `retry()`; re-runs the start effect.
  const [startAttempt, setStartAttempt] = useState(0);

  // Session start (and retry): restore from the refresh cookie. The work is
  // single-flight and never aborted (see `auth.service.ts`), so StrictMode's double
  // invocation does it once; `ignore` only stops a superseded run from writing state.
  useEffect(() => {
    let ignore = false;

    authService.startSession().then(
      (user) => {
        if (!ignore) {
          setSession(user === null ? SIGNED_OUT : { ...LOADING, status: 'authenticated', user });
        }
      },
      () => {
        // The API could not be reached, which says nothing about whether a session
        // exists.
        if (!ignore) {
          setSession({ ...LOADING, status: 'unavailable' });
        }
      },
    );

    return () => {
      ignore = true;
    };
  }, [startAttempt]);

  /*
   * Expired-session path. The HTTP client calls this once when a request is rejected
   * with a session code and the silent refresh also fails. Dropping to signed-out
   * here means the route guard redirects to /login exactly once — the sign-in and
   * refresh endpoints never trigger it, so there is no loop. Registered in an effect
   * because it subscribes to a module outside React.
   */
  useEffect(
    () =>
      setSessionExpiredHandler(() => {
        accessTokenStore.clear();
        setSession((previous) =>
          previous.status === 'authenticated' ? { ...SIGNED_OUT, endReason: 'expired' } : previous,
        );
      }),
    [],
  );

  const value: AuthContextValue = {
    ...session,
    can: (capability) => hasCapability(session.user, capability),
    canUseAdminArea: canUseAdminArea(session.user),

    async signInWithMicrosoft(returnTo) {
      setSession((previous) => ({ ...previous, signInFailure: null, endReason: null }));

      try {
        const signIn = await authService.signInWithMicrosoft(returnTo);
        setSession({ ...LOADING, status: 'authenticated', ...signIn });
      } catch (error) {
        // A failed sign-in is a signed-out state with a reason to show.
        setSession((previous) => ({
          ...previous,
          signInFailure: isSignInError(error) ? error.failure : 'microsoft-failed',
        }));
      }
    },

    async logout() {
      const { serverConfirmed } = await authService.logout();
      setSession({
        ...SIGNED_OUT,
        endReason: serverConfirmed ? 'signed-out' : 'signed-out-unconfirmed',
      });
    },

    async reloadUser() {
      const user = await authService.getCurrentUser();
      setSession((previous) =>
        previous.status === 'authenticated' ? { ...previous, user } : previous,
      );
    },

    retry() {
      setSession(LOADING);
      setStartAttempt((attempt) => attempt + 1);
    },

    // DEV-1: development-only sign-in with a real API session. `null` in production.
    signInAsDevUser:
      import.meta.env.DEV && isDevAuthEnabled()
        ? async (role) => {
            // The user comes from `/auth/me`; `role` is not kept anywhere.
            const user = await devLogin(role);
            setSession({ ...LOADING, status: 'authenticated', user });
          }
        : null,
  };

  return <AuthContext value={value}>{children}</AuthContext>;
}
